/**
 * Form primitives: TextField, TextArea, Select, Switch (prompt.md §37, §53, §55).
 *
 * WHY each control wires its own label, hint and error message: BYOK keys,
 * Postgres connection strings and tool permissions are entered by people who
 * need to be told exactly what is wrong with an input, and every screen must do
 * that the same way. A `SecretField` variant masks a value that can never be
 * read back (§13).
 */

import { useId, type ChangeEvent, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes, type InputHTMLAttributes } from "react";

import { FOCUS_BORDER, FOCUS_RING, NO_RING, fieldStyle, LABEL_TEXT, MUTED_TEXT, rowStyle } from "../styles.ts";

interface FieldShellProps {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | null;
  readonly optional?: boolean;
  readonly htmlFor: string;
  readonly children: ReactNode;
}

function FieldShell({ label, hint, error, optional, htmlFor, children }: FieldShellProps) {
  return (
    <div>
      <label style={LABEL_TEXT} htmlFor={htmlFor}>
        {label}
        {optional ? <span style={{ ...MUTED_TEXT, marginLeft: "var(--dayvise-space-xs)" }}>optional</span> : null}
      </label>
      {children}
      {error ? (
        <span role="alert" style={{ display: "block", marginTop: "var(--dayvise-space-xxs)", color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-xs)" }}>
          {error}
        </span>
      ) : hint ? (
        <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)", display: "block", marginTop: "var(--dayvise-space-xxs)" }}>{hint}</span>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "className" | "style"> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | null;
  readonly optional?: boolean;
  /**
   * Renders a masked input. The server never returns a stored secret, so this is
   * a write-only field by design (prompt.md §13).
   */
  readonly secret?: boolean;
}

export function TextField({ label, hint, error, optional, secret, id, ...rest }: TextFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <FieldShell label={label} hint={hint} error={error} optional={optional} htmlFor={fieldId}>
      <input
        {...rest}
        id={fieldId}
        type={secret ? "password" : (rest.type ?? "text")}
        aria-invalid={error ? true : undefined}
        autoComplete={secret ? "off" : rest.autoComplete}
        style={fieldStyle({ invalid: Boolean(error), disabled: rest.disabled })}
        onFocus={(event) => {
          event.currentTarget.style.borderColor = FOCUS_BORDER;
          event.currentTarget.style.boxShadow = FOCUS_RING;
          rest.onFocus?.(event);
        }}
        onBlur={(event) => {
          event.currentTarget.style.borderColor = error ? "var(--dayvise-color-danger)" : "var(--dayvise-color-border)";
          event.currentTarget.style.boxShadow = NO_RING;
          rest.onBlur?.(event);
        }}
      />
    </FieldShell>
  );
}

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "className" | "style"> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | null;
  readonly optional?: boolean;
}

export function TextArea({ label, hint, error, optional, id, rows = 5, ...rest }: TextAreaProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <FieldShell label={label} hint={hint} error={error} optional={optional} htmlFor={fieldId}>
      <textarea
        {...rest}
        id={fieldId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        style={{ ...fieldStyle({ invalid: Boolean(error), disabled: rest.disabled }), resize: "vertical", fontFamily: "var(--dayvise-font-family-sans)" }}
      />
    </FieldShell>
  );
}

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "className" | "style" | "children"> {
  readonly label: string;
  readonly options: readonly SelectOption[];
  readonly hint?: string;
  readonly error?: string | null;
}

export function Select({ label, options, hint, error, id, ...rest }: SelectProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <FieldShell label={label} hint={hint} error={error} htmlFor={fieldId}>
      <select {...rest} id={fieldId} style={fieldStyle({ invalid: Boolean(error), disabled: rest.disabled })}>
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

export interface SwitchProps {
  readonly label: string;
  readonly description?: string;
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly onChange: (checked: boolean) => void;
}

export function Switch({ label, description, checked, disabled = false, onChange }: SwitchProps) {
  const handleToggle = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange(event.target.checked);
  };
  return (
    <label style={{ ...rowStyle("var(--dayvise-space-sm)"), alignItems: "flex-start", cursor: disabled ? "not-allowed" : "pointer" }}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={handleToggle}
        style={{
          appearance: "none",
          width: "34px",
          height: "20px",
          margin: 0,
          marginTop: "2px",
          borderRadius: "var(--dayvise-radius-pill)",
          border: `1px solid ${checked ? "var(--dayvise-color-accent)" : "var(--dayvise-color-border)"}`,
          background: checked ? "var(--dayvise-color-accent)" : "var(--dayvise-color-surface)",
          position: "relative",
          cursor: "inherit",
          transition: "background var(--dayvise-motion-duration-fast) var(--dayvise-motion-easing-standard)",
        }}
      />
      <span>
        <span style={{ display: "block", color: "var(--dayvise-color-text-primary)", fontSize: "var(--dayvise-font-size-sm)", fontWeight: "var(--dayvise-font-weight-medium)" }}>
          {label}
        </span>
        {description === undefined ? null : <span style={{ ...MUTED_TEXT, display: "block" }}>{description}</span>}
      </span>
    </label>
  );
}
